import React from "react";
import { format, parseISO } from "date-fns";

// "WorkOrder" -> "Work Order", "TaxAdjustment" -> "Tax Adjustment",
// "SUPPLIER_INVOICE" -> "Supplier Invoice"
function formatTranType(raw) {
   if (!raw) return "";
   return String(raw)
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
}

function fmtAmount(value) {
   const n = Number(value);
   return Number.isFinite(n) ? n.toFixed(2) : "";
}

function fmtPostedDate(iso) {
   if (!iso) return "";
   try {
      return format(parseISO(iso), "yyyy-MM-dd");
   } catch {
      return "";
   }
}

function ConfidenceBadge({ confidence }) {
   if (confidence === null || confidence === undefined) return null;
   const pct = Math.round(Number(confidence) * 100);
   if (!Number.isFinite(pct)) return null;
   return <span className={`recon-conf${pct < 90 ? " low" : ""}`}>{pct}%</span>;
}

function StatementPane({ rowData, stmt }) {
   return (
      <div className="recon-pane">
         <h4>Supplier Statement</h4>
         <dl className="recon-fields">
            <dt>Invoice #</dt>
            <dd>
               {rowData.sd_parsed_invoice_no || rowData.sd_invoice_no || "—"}
               {rowData.sd_invoice_no && <span className="recon-scan">scanned "{rowData.sd_invoice_no}"</span>}
               <ConfidenceBadge confidence={stmt?.invoice_id?.confidence} />
            </dd>
            <dt>Date</dt>
            <dd>
               {rowData.sd_invoice_date || "—"}
               {stmt?.invoice_date?.value && <span className="recon-scan">scanned "{stmt.invoice_date.value}"</span>}
               <ConfidenceBadge confidence={stmt?.invoice_date?.confidence} />
            </dd>
            <dt>Amount</dt>
            <dd>
               {fmtAmount(rowData.sd_amount) || "—"}
               {stmt?.amount?.value && <span className="recon-scan">scanned "{stmt.amount.value}"</span>}
               <ConfidenceBadge confidence={stmt?.amount?.confidence} />
            </dd>
            <dt>PO No</dt>
            <dd>
               {rowData.sd_po_no || "—"}
               {stmt?.po_no?.value && <span className="recon-scan">scanned "{stmt.po_no.value}"</span>}
               <ConfidenceBadge confidence={stmt?.po_no?.confidence} />
            </dd>
         </dl>
      </div>
   );
}

function ProtractorSkeleton() {
   return (
      <div className="recon-skeleton">
         <div className="recon-sk" style={{ width: "55%" }} />
         <div className="recon-sk" style={{ width: "45%" }} />
         <div className="recon-sk" style={{ width: "60%" }} />
         <div className="recon-sk recon-sk-gap" style={{ width: "90%" }} />
         <div className="recon-sk" style={{ width: "75%" }} />
         <div className="recon-sk" style={{ width: "80%" }} />
      </div>
   );
}

function ProtractorLoaded({ rowData, erp }) {
   const details = Array.isArray(erp?.details) ? erp.details : [];
   const lineTotal = details.reduce((sum, d) => sum + (Number(d.total_amount) || 0), 0);
   const total = details.length ? lineTotal : Number(rowData.en_invoice_amount) || 0;

   return (
      <>
         <dl className="recon-fields">
            <dt>Invoice #</dt>
            <dd>{rowData.esh_parsed_invoice_id || rowData.en_invoice_id || "—"}</dd>
            <dt>Tran type</dt>
            <dd>{formatTranType(erp?.tran_type) || "—"}</dd>
            <dt>Invoice date</dt>
            <dd>{erp?.invoice_date || "—"}</dd>
            <dt>Total tax</dt>
            <dd>{fmtAmount(erp?.total_tax) || "—"}</dd>
         </dl>
         {details.length === 0 ? (
            <div className="recon-li-empty">No line items.</div>
         ) : (
            <table className="recon-li">
               <thead>
                  <tr>
                     <th>Type</th>
                     <th>Description</th>
                     <th className="num">Amount</th>
                  </tr>
               </thead>
               <tbody>
                  {details.map((d) => {
                     const isTax = /tax/i.test(d.tran_type || "");
                     return (
                        <tr key={d.eshd_id}>
                           <td>
                              <span className={`recon-tt${isTax ? " tax" : ""}`}>{formatTranType(d.tran_type)}</span>
                           </td>
                           <td>{d.description}</td>
                           <td className="num">{fmtAmount(d.total_amount)}</td>
                        </tr>
                     );
                  })}
               </tbody>
               <tfoot>
                  <tr>
                     <td colSpan={2} className="recon-li-total-lbl">
                        Total
                     </td>
                     <td className="num">{fmtAmount(total)}</td>
                  </tr>
               </tfoot>
            </table>
         )}
         {(erp?.user_name || erp?.inserted_on) && (
            <div className="recon-erp-meta">
               Posted by {erp?.user_name || "—"}
               {erp?.inserted_on ? ` · ${fmtPostedDate(erp.inserted_on)}` : ""}
            </div>
         )}
      </>
   );
}

function ProtractorPane({ rowData, detail, onRetry }) {
   const status = detail?.status;
   const erp = detail?.data?.erp_detail;
   const hasErp = erp && typeof erp === "object";
   // A row can now load with statement_detail but no ERP match (so the Statement pane gets its
   // scanned-confidence badges). Treat "loaded but no ERP" the same as the old "empty" status.
   const isEmpty = status === "empty" || (status === "loaded" && !hasErp);
   return (
      <div className="recon-pane erp">
         <h4>
            Protractor Entry
            {status === "loading" && <span className="recon-spinner" />}
         </h4>
         {status === "loading" && <ProtractorSkeleton />}
         {isEmpty && <div className="recon-empty">No Protractor entry is matched to this statement line.</div>}
         {status === "error" && (
            <div className="recon-empty">
               Couldn't load Protractor detail.
               <button type="button" className="recon-retry" onClick={onRetry}>
                  Retry
               </button>
            </div>
         )}
         {status === "loaded" && hasErp && <ProtractorLoaded rowData={rowData} erp={erp} />}
      </div>
   );
}

// showStatement / showErp default to true so the Reconcile page (which always wants both
// panes side-by-side, aligned under their column groups) is unchanged. The Invoices radar
// passes `totalCols` to opt into a self-contained layout: one full-width cell holding a
// two-column grid, so the panes split evenly (50/50 when matched) and sit flush-left
// instead of being mapped onto the table's uneven fixed-width columns.
function ReconcileLineDetailPanel({
   rowData,
   detail,
   onRetry,
   leftSpan = 6,
   rightSpan = 4,
   showStatement = true,
   showErp = true,
   totalCols,
}) {
   if (totalCols != null) {
      // No `recon-detail-cell` class here: its left/right borders would box the full-width
      // cell into an outlined "highlight". The Reconcile page wants that boxing (its cell sits
      // between spacer columns); the Invoices radar wants the detail to blend like Reconcile's
      // does, so we keep only the padding/alignment.
      return (
         <tr className="recon-detail-row">
            <td colSpan={totalCols} style={{ padding: "10px 12px 14px", verticalAlign: "top" }}>
               <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "32px", alignItems: "start" }}>
                  {showStatement && (
                     <div style={{ minWidth: 0 }}>
                        <StatementPane rowData={rowData} stmt={detail?.data?.statement_detail} />
                     </div>
                  )}
                  {showErp && (
                     <div style={{ minWidth: 0 }}>
                        <ProtractorPane rowData={rowData} detail={detail} onRetry={onRetry} />
                     </div>
                  )}
               </div>
            </td>
         </tr>
      );
   }

   // Reconcile layout: panes aligned under their column groups via spacer cells.
   return (
      <tr className="recon-detail-row">
         <td className="recon-detail-spacer" />
         <td colSpan={leftSpan} className="recon-detail-cell">
            <StatementPane rowData={rowData} stmt={detail?.data?.statement_detail} />
         </td>
         <td className="recon-detail-spacer" />
         <td colSpan={rightSpan} className="recon-detail-cell">
            <ProtractorPane rowData={rowData} detail={detail} onRetry={onRetry} />
         </td>
         <td className="recon-detail-spacer" />
      </tr>
   );
}

export default ReconcileLineDetailPanel;
